import React, { Component, ReactNode } from 'react';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';

import TopBar from 'components/Header/TopBar/TopBar';
import Header from 'components/Header/Header';

import EntityCompareHeader from 'components/Header/EntityCompareHeader/EntityCompareHeader';
import Content from 'components/styled/Content/Content';
import ErrorBoundary from 'components/utility/ErrorBoundary/ErrorBoundary';

import ScrollTopOnMount from 'components/utility/ScrollTopOnMount/ScrollTopOnMount';
import InViewObserver from 'components/utility/InViewObserver/InViewObserver';
import BackToTop from 'components/BackToTop/BackToTop';
import Footer from 'components/Footer/Footer';
import TopicNavigation from 'components/TopicNavigation/TopicNavigation';

import { EntityContext, defaultColors, IEntity } from 'context/entityContext';

import pluralize from 'helpers/pluralize';
import getFiltersFromUrl from 'helpers/getFiltersFromUrl';
import getPathPrefix from 'helpers/topicNavigation/getPathPrefix';

import { shouldLoadEntity, loadEntity } from 'redux/reducers/entity';
import EntityContent from 'pages/Entity/Content';
import NotificationSettingsModal from 'components/notifications/Settings';
import { isArray } from 'lodash';
import {
  loadCustomRegions,
  shouldLoadCustomRegions,
} from 'redux/reducers/customRegions';

/**
 * The Entity component renders the correct Entity component (Track, Release, Artist, Label, etc.)
 * It also renders the components that are shared between entities.
 * @param {object} props Object of properties, passed down by the parent React.Component
 * @param {object} props.match Object with routing params, passed down by BrowserRouter
 */
class EntityCompare extends Component<Record<string, unknown>, any> {
  constructor(props: any) {
    super(props);

    let { entityIds } = props.match.params;
    entityIds = entityIds ? entityIds.split(',') : [];

    this.state = {
      entityIds,
      compareColors: defaultColors.compareColors,
      pageInview: true,
    };

    if (props.shouldLoadCustomRegions()) {
      props.loadCustomRegions();
    }
  }

  static getDerivedStateFromProps(
    newProps: any,
    oldState: any
  ): {
    entityIds: string[];
    compareColors?: string[];
  } {
    let { entityIds } = newProps.match.params;
    entityIds = entityIds ? entityIds.split(',') : [];

    /** Check if an ID is removed from the previous entityIds array and return its index. */
    const removedIndex = oldState.entityIds.findIndex((item: any) => {
      return !entityIds.includes(item);
    });

    /**
     * If an index is found create a new array in which the value at the removedIndex is placed at the end of the array
     * Dont update the colors when the user removes the last item of the array.
     */
    if (removedIndex > -1 && removedIndex !== oldState.entityIds.length - 1) {
      const slicedValue = oldState.compareColors[removedIndex];

      const newcompareColors = oldState.compareColors.filter(
        (val: any, index: number) => {
          return index !== removedIndex;
        }
      );
      /** Place the sliced value at the next positionin the colorArray. So when the user removes the yellow item, the next item it adds will be yellow again. */
      newcompareColors.splice(oldState.entityIds.length - 1, 0, slicedValue);

      return {
        entityIds,
        compareColors: newcompareColors,
      };
    }

    return {
      entityIds,
    };
  }

  loadAllEntities(): void {
    const props: any = this.props;
    const { match, shouldLoadEntity, loadEntity } = props;
    const { entityType } = match.params;
    let { entityIds } = match.params;
    entityIds = entityIds.split(',');

    entityIds.forEach((id: any) => {
      if (shouldLoadEntity(entityType, id)) {
        loadEntity(
          entityType,
          id,
          entityType !== 'artist' && entityType !== 'label'
        );
      }
    });
  }

  /**
   * Reload entity based on its entity id. This function is used when an entity
   * fails to load. The user can decide to retry and load this entity again.
   */
  reloadEntity = (entityId: string): void => {
    const props: any = this.props;
    const { match, loadEntity } = props;
    const { entityType } = match.params;

    /**
     * Because we want to reload the entity we want to immediately load it and
     * not first check if it should load.
     */
    loadEntity(
      entityType,
      entityId,
      entityType !== 'artist' && entityType !== 'label'
    );
  };

  handleScroll = (): void => {
    if (this.state.pageInview !== window.pageYOffset < 125) {
      this.setState({ pageInview: window.pageYOffset < 125 });
    }
  };

  componentDidMount(): void {
    this.loadAllEntities();
  }

  componentWillUnmount(): void {
    window.removeEventListener('scroll', this.handleScroll);
  }

  componentDidUpdate(): void {
    this.loadAllEntities();
    window.addEventListener('scroll', this.handleScroll);
  }

  render(): ReactNode {
    const props: any = this.props;
    const state: any = this.state;
    const { compareColors } = state;
    const {
      match,
      location,
      history,
      compareMode,
      entitiesById,
      customRegions,
    } = props;
    const { entityType, topic, subTopic, parentEntityId } = match.params;

    let { entityIds } = match.params;
    entityIds = entityIds.split(',');

    // If there are no entities loaded at all, bail out.
    if (!entitiesById) return null;

    /** Check if all entityIds are set in Redux, otherwise bail out. */
    if (!entityIds.every((id: any) => id in entitiesById)) {
      return null;
    }

    const title = `Compare ${pluralize(entityType)}`;

    /** Get all the ActiveFilters from the URL */
    const filters = getFiltersFromUrl(location);

    const pathPrefix = getPathPrefix(compareMode);

    const customRegionFilter =
      getFiltersFromUrl(location)?.customRegion && !isArray(customRegions)
        ? getFiltersFromUrl(location)?.customRegion
        : undefined;

    const context: IEntity = {
      entityType,
      entityIds,
      countryIds: undefined,
      compareMode,
      topic: subTopic ? [topic, subTopic] : [topic],
      parentEntityId,
      filters,
      isVersion: false,
      dataColors: compareColors,
      customRegionFilter,
    };

    return (
      <>
        <ScrollTopOnMount />
        <ErrorBoundary type="page">
          <Helmet defer={false} title={title} />
          <NotificationSettingsModal />

          <EntityContext.Provider value={context}>
            <InViewObserver
              render={({ inview }: any): ReactNode => (
                <Header inView={inview} withVerticalBackground>
                  <TopBar isStatic />

                  <TopBar
                    entityTitle={pluralize(entityType)}
                    topicEntityType="compare"
                    inView={state.pageInview}
                    isSticky
                    entitiesById={entitiesById}
                    noComparedItems={false}
                    showOnlyOnScroll
                  />

                  {compareMode === 'entity' && (
                    <EntityCompareHeader
                      entityIds={entityIds}
                      entityType={entityType}
                      entitiesById={entitiesById}
                      topic={context.topic}
                      history={history}
                      reloadEntity={this.reloadEntity}
                    />
                  )}
                  {compareMode === 'entity' && (
                    <TopicNavigation
                      activeTopic={topic}
                      pathPrefix={pathPrefix}
                    />
                  )}
                </Header>
              )}
            />

            <Content>
              <EntityContent loading={false} />
            </Content>
            <BackToTop />
            <Footer />
          </EntityContext.Provider>
        </ErrorBoundary>
      </>
    );
  }
}

export default connect(
  (state: any, ownProps: any) => {
    const { entityType } = ownProps.match.params;
    return {
      entitiesById: state.entity.byId[entityType],
      customRegions: state.customRegions.data,
    };
  },
  {
    shouldLoadEntity,
    loadEntity,
    shouldLoadCustomRegions,
    loadCustomRegions,
  }
)(EntityCompare);
